/**
 * A recipe document → its flavor wheels and, when its style has bands, how
 * close it sits (PLAN-FLAVOR). The one place catalog scores meet recipe rows.
 */

import {
  fermentableScores,
  getYeast,
  hopScores,
  maltPotency,
  styleBands,
  yeastScores,
} from '@beerwright/catalog';
import type {
  FlavorFit,
  FlavorProfile,
  RecipeCalculation,
  StyleFlavorBands,
} from '@beerwright/core';
import { fermentTempOutside, flavorFit, flavorProfile } from '@beerwright/core';
import type { RecipeDoc } from '../document/recipe';

export type StyleFlavorState = 'scored' | 'no-style' | 'not-authored' | 'no-profile';

/** A yeast as the fermentation wheel read it: its name and the range it tilted against. */
export interface FlavorFermentationYeast {
  readonly name: string;
  /** The published range, **°C**, or `null` when neither the row nor the catalog has one. */
  readonly rangeC: { readonly min: number; readonly max: number } | null;
}

/**
 * What the fermentation wheel was tilted by, so a surface can say *why* a
 * yeast reads off style without working the temperature and ranges out a
 * second time and getting a different answer.
 */
export interface FlavorFermentation {
  /** The first primary step's temperature, **°C**, or `null` with no primary. */
  readonly tempC: number | null;
  /** One per yeast row, in recipe order, named as the wheel's causes name them. */
  readonly yeasts: readonly FlavorFermentationYeast[];
}

export interface RecipeFlavor {
  readonly profile: FlavorProfile;
  readonly bands: StyleFlavorBands | null;
  readonly fit: FlavorFit | null;
  readonly state: StyleFlavorState;
  readonly fermentation: FlavorFermentation;
}

export function recipeFlavor(doc: RecipeDoc, calculation: RecipeCalculation): RecipeFlavor {
  const primary = doc.fermentationSteps.find((step) => step.type === 'primary');
  const yeasts = doc.yeasts.map((row) => {
    // The row's own published range first: it is on custom cultures too.
    const entry = row.catalogRef === null ? undefined : getYeast(row.catalogRef.id);
    const rangeC =
      row.temperatureMinC !== null && row.temperatureMaxC !== null
        ? { min: row.temperatureMinC, max: row.temperatureMaxC }
        : (entry?.temperatureC ?? null);
    return { row, rangeC };
  });
  const fermentation: FlavorFermentation = {
    tempC: primary?.tempC ?? null,
    yeasts: yeasts.map(({ row, rangeC }) => ({ name: row.name, rangeC })),
  };
  const profile = flavorProfile({
    volumeL: calculation.volumes.volumeIntoFermenterL,
    hops: doc.hops.map((row) => ({
      name: row.name,
      amountKg: row.amountKg,
      use: row.use,
      timeMinutes: row.timeMinutes,
      ...(row.whirlpoolTempC === null ? {} : { whirlpoolTempC: row.whirlpoolTempC }),
      scores: row.catalogRef === null ? null : hopScores(row.catalogRef.id),
    })),
    malts: doc.fermentables.map((row) => ({
      name: row.name,
      amountKg: row.amountKg,
      potency: maltPotency(row.maltClass, row.colorLovibond),
      scores: fermentableScores({
        catalogId: row.catalogRef?.id ?? null,
        maltClass: row.maltClass,
        colorLovibond: row.colorLovibond,
      }),
    })),
    yeasts: yeasts.map(({ row, rangeC }) => ({
      name: row.name,
      scores: yeastScores({ catalogId: row.catalogRef?.id ?? null, type: row.type }),
      rangeC,
    })),
    fermentTempC: fermentation.tempC,
  });

  const unscored = { profile, bands: null, fit: null, fermentation } as const;
  if (doc.styleId === null) return { ...unscored, state: 'no-style' };
  const bands = styleBands(doc.styleId);
  if (bands === undefined) return { ...unscored, state: 'not-authored' };
  if (bands === null) return { ...unscored, state: 'no-profile' };
  return { profile, bands, fit: flavorFit(profile.values, bands), state: 'scored', fermentation };
}

/** A yeast fermented outside its published range: where, against what, and which way. */
export interface FlavorOutOfRange {
  readonly tempC: number;
  readonly rangeC: { readonly min: number; readonly max: number };
  readonly direction: 'below' | 'above';
}

/**
 * Whether the yeast of this name was fermented outside its range, by the same
 * measure and tolerance the Checks card uses (`fermentTempOutside`), so the
 * breakdown never blames a temperature the checks call fine.
 *
 * `null` inside the range, with no primary temperature, with no range, or for
 * a name that is not one of the recipe's yeasts. Two rows with one name answer
 * for the first, as the wheel's causes already sum them under the name.
 */
export function flavorOutOfRange(flavor: RecipeFlavor, yeastName: string): FlavorOutOfRange | null {
  const { tempC } = flavor.fermentation;
  const yeast = flavor.fermentation.yeasts.find((entry) => entry.name === yeastName);
  if (tempC === null || yeast === undefined || yeast.rangeC === null) return null;
  const direction = fermentTempOutside(tempC, yeast.rangeC);
  return direction === null ? null : { tempC, rangeC: yeast.rangeC, direction };
}
